import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import {
  BiweeklyPeriod,
  type PayoutFrequency,
  PayoutFrequencyType,
  WeeklyAnchorStripe,
} from "@modules/box/boxPayoutFrequencyTypes"
import { type PaymentMethodPreference } from "@modules/box/boxSettingsType"
import { word } from "@modules/core/utils/i18n"
import { PaymentMethodMode, PaymentMethodType } from "@modules/finances/types/financesType"
import { z } from "zod"

export type BoxSettingsFormFields = {
  paymentMethodPreferences: PaymentMethodPreference[]
  slotGeneratorInWeeks: number
  showSessionAttendees: boolean
  payoutFrequency: PayoutFrequency
}

export type BoxWaiverFormFields = {
  waiver: MediaItemFormType | null
}

export const getBoxSettingsSchema = () =>
  z.object({
    paymentMethodPreferences: z
      .array(
        z.object({
          type: z.enum(PaymentMethodType),
          mode: z.enum(PaymentMethodMode),
          enabled: z.boolean(),
        }),
      )
      .refine((value) => {
        const subscriptionMethods = value.filter((method) => method.mode === PaymentMethodMode.SUBSCRIPTION)
        return subscriptionMethods.length === 0 || subscriptionMethods.some((method) => method.enabled)
      }, word("form.required.atLeastOne")),
    slotGeneratorInWeeks: z
      .number({ message: word("global.form.required") })
      .min(2, word("global.form.minimumValue", { minValue: 2 }))
      .max(52, word("global.form.maximumValue", { maxValue: 52 })),
    showSessionAttendees: z.boolean(),
    payoutFrequency: z
      .object({
        type: z.enum(PayoutFrequencyType),
        dayCalendar: z.number().min(1).max(31).optional(),
        weeklyAnchorStripe: z.enum(WeeklyAnchorStripe).optional(),
        biweeklyPeriod: z.enum(BiweeklyPeriod).optional(),
      })
      .superRefine((value, ctx) => {
        if (value.type === PayoutFrequencyType.MONTHLY && value.dayCalendar == null) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["dayCalendar"] })
        }
        if (value.type === PayoutFrequencyType.WEEKLY && value.weeklyAnchorStripe == null) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["weeklyAnchorStripe"] })
        }
        if (value.type === PayoutFrequencyType.BIWEEKLY && value.biweeklyPeriod == null) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["biweeklyPeriod"] })
        }
      }),
  })

const getInvalidFilenameChars = (value: string) =>
  Array.from(new Set(value.split("").filter((char) => !/[a-zA-Z0-9 _-]/.test(char))))

export const getBoxWaiverSchema = () =>
  z.object({
    waiver: z
      .object({
        url: z.string().nullable(),
        key: z.string().nullable(),
        file: z.instanceof(File).nullable(),
        type: z.enum(MediaType),
        isUploading: z.boolean(),
        filename: z.string().nullable(),
      })
      .nullable()
      .superRefine((waiver, ctx) => {
        if (waiver == null) return

        const filename = waiver.filename ?? ""
        if (filename.length < 1) {
          ctx.addIssue({ code: "custom", message: word("global.form.required"), path: ["filename"] })
          return
        }
        if (filename.length > 255) {
          ctx.addIssue({ code: "custom", message: word("form.maximum.characters", { count: 50 }), path: ["filename"] })
        }
        const invalidChars = getInvalidFilenameChars(filename)
        if (invalidChars.length > 0) {
          ctx.addIssue({
            code: "custom",
            message: `${word("box.form.agreementDocument.name.invalid")}: ${invalidChars.join(" ")}`,
            path: ["filename"],
          })
        }
      }),
  })
